import React, { useState, useEffect } from 'react';
import axios from 'axios';

function AdminUserTab() {
  const [users, setUsers] = useState([]);
  const [newUser, setNewUser] = useState({ username: '', email: '', password: '', role: 'admin' });
  const [editingUser, setEditingUser] = useState(null);
  const [error, setError] = useState('');
  const [passwordError, setPasswordError] = useState('');

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await axios.get('/api/auth/users', { withCredentials: true });
      setUsers(response.data);
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to fetch users');
    }
  };

  /**
   * Validates password strength against security policy
   * @param {string} password - The password to validate
   * @returns {Object} - Object with isValid boolean and message string
   */
  const validatePassword = (password) => {
    // Skip validation if updating user and password field is empty
    if (editingUser && !password) {
      return { isValid: true, message: '' };
    }
    
    // Check for minimum length (8 characters)
    if (!password || password.length < 8) {
      return {
        isValid: false,
        message: 'Password must be at least 8 characters long'
      };
    }

    // Check for uppercase letters
    if (!/[A-Z]/.test(password)) {
      return {
        isValid: false,
        message: 'Password must contain at least one uppercase letter'
      };
    }

    // Check for lowercase letters
    if (!/[a-z]/.test(password)) {
      return {
        isValid: false,
        message: 'Password must contain at least one lowercase letter'
      };
    }

    // Check for numbers
    if (!/\d/.test(password)) {
      return {
        isValid: false,
        message: 'Password must contain at least one number'
      };
    }

    // Check for special characters
    if (!/[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]/.test(password)) {
      return {
        isValid: false,
        message: 'Password must contain at least one special character'
      };
    }

    return {
      isValid: true,
      message: ''
    };
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Clear password error when the password field changes
    if (name === 'password') {
      setPasswordError('');
    }
    
    if (editingUser) {
      setEditingUser({ ...editingUser, [name]: value });
    } else {
      setNewUser({ ...newUser, [name]: value });
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // Get the password to validate
    const passwordToValidate = editingUser ? editingUser.password : newUser.password;
    
    // Validate password
    const passwordValidation = validatePassword(passwordToValidate);
    
    if (!passwordValidation.isValid) {
      setPasswordError(passwordValidation.message);
      return; // Prevent submission if password is invalid
    }
    
    try {
      if (editingUser) {
        // If editing and password is empty, remove it from the request
        const userData = { ...editingUser };
        if (!userData.password) {
          delete userData.password;
        }
        await axios.put(`/api/auth/users/${editingUser._id}`, userData, { withCredentials: true });
        setEditingUser(null);
      } else {
        await axios.post('/api/auth/register', newUser, { withCredentials: true });
        setNewUser({ username: '', email: '', password: '', role: 'admin' });
      }
      fetchUsers();
      setError('');
      setPasswordError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Operation failed');
    }
  };

  const handleEdit = (user) => {
    setEditingUser({ ...user, password: '' }); // Password not pre-filled for security
    setError('');
    setPasswordError('');
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this admin?')) return;
    try {
      await axios.delete(`/api/auth/users/${id}`, { withCredentials: true });
      fetchUsers();
      setError('');
    } catch (err) {
      setError(err.response?.data?.message || 'Failed to delete user');
    }
  };

  return (
    <div className="max-w-[1280px] mx-auto p-4 md:p-8 bg-[#1a1a1a] rounded-lg text-white flex flex-col gap-4">
      <h2 className="text-xl text-[#8C0000] font-cinzel uppercase font-normal text-center md:text-left mb-4">
        {editingUser ? 'Edit Admin' : 'Add New Admin'}
      </h2>
      {error && <p className="text-[#f44336] text-center mb-4">{error}</p>}
      <form onSubmit={handleSubmit} className="flex flex-col gap-4 mb-8">
        <input
          type="text"
          name="username"
          value={editingUser ? editingUser.username : newUser.username}
          onChange={handleChange}
          placeholder="Username"
          className="p-2 bg-[#333] border border-[#555] rounded text-white"
          required
        />
        <input
          type="email"
          name="email"
          value={editingUser ? editingUser.email : newUser.email}
          onChange={handleChange}
          placeholder="Email"
          className="p-2 bg-[#333] border border-[#555] rounded text-white"
          required
        />
        <div className="flex flex-col">
          <input
            type="password"
            name="password"
            value={editingUser ? editingUser.password : newUser.password}
            onChange={handleChange}
            placeholder={editingUser ? 'New Password (optional)' : 'Password'}
            className={`p-2 bg-[#333] border ${passwordError ? 'border-[#f44336]' : 'border-[#555]'} rounded text-white`}
            required={!editingUser}
          />
          {passwordError && <p className="text-[#f44336] text-sm mt-1">{passwordError}</p>}
          <p className="text-[#999] text-xs mt-1">
            Password must be at least 8 characters long and include uppercase, lowercase, 
            numbers, and special characters.
          </p>
        </div>
        <select
          name="role"
          value={editingUser ? editingUser.role : newUser.role}
          onChange={handleChange}
          className="p-2 bg-[#333] border border-[#555] rounded text-white"
        >
          <option value="admin">Admin</option>
          <option value="superadmin">Super Admin</option>
        </select>
        <div className="flex gap-2">
          <button
            type="submit"
            className="px-4 py-2 bg-[#d4a017] hover:bg-[#b38b00] text-white rounded transition-colors"
          >
            {editingUser ? 'Update Admin' : 'Add Admin'}
          </button>
          {editingUser && (
            <button
              type="button"
              onClick={() => setEditingUser(null)}
              className="px-4 py-2 bg-[#666] hover:bg-[#555] text-white rounded transition-colors"
            >
              Cancel
            </button>
          )}
        </div>
      </form>

      <h3 className="text-lg text-[#8C0000] font-cinzel uppercase font-normal text-center md:text-left mb-4">Admin Users</h3>
      {users.length === 0 ? (
        <p className="text-[#ccc] text-center">No users available</p>
      ) : (
        <ul className="list-none p-0">
          {users.map((user) => (
            <li key={user._id} className="flex flex-col md:flex-row justify-between items-center py-2 border-b border-[#444]">
              <span className="text-white flex-1 text-center md:text-left">
                {user.username} ({user.role})
              </span>
              <div className="flex flex-row gap-2 mt-2 md:mt-0">
                <button
                  onClick={() => handleEdit(user)}
                  className="px-3 py-1 bg-[#4CAF50] hover:bg-[#45a049] text-white text-sm rounded transition-colors"
                >
                  Edit
                </button>
                <button
                  onClick={() => handleDelete(user._id)}
                  className="px-3 py-1 bg-[#f44336] hover:bg-[#da190b] text-white text-sm rounded transition-colors"
                >
                  Delete
                </button>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

export default AdminUserTab;